import React, { useEffect, useMemo, useState } from "react";
import { axiosSalary, axiosMaster } from "../api/axiosInstance";
import Select from "react-select";
import { FaSearch, FaFileExcel } from "react-icons/fa";
import { useAuth } from "../auth/AuthContext";
import AddSessionByManager from "./AddSessionByManager"; // ✅ IMPORT
import ReplaceSessionByManager from "./ReplaceSessionByManager";
import LockAttendance from "./LockAttendance";
import UnlockAttendance from "./UnlockAttendance";
import RemoveSessionByManager from "./RemoveSessionByManager";
import FinalizeAttendance from "./FinalizeAttendance";
import UnfinalizeAttendance from "./UnfinalizeAttendance";
import EditSessionByManager from "./EditSessionByManager";

const AttendanceExceptionReport = () => {

  /* ================= AUTH ================= */
  const { user } = useAuth();
  const groupId = user?.groupId;
  const restId = user?.restaurantId;

  /* ================= STATE ================= */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sessionOpen, setSessionOpen] = useState(false); // ✅
  const [selectedRow, setSelectedRow] = useState(null);  // ✅
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [selectedReplaceRow, setSelectedReplaceRow] = useState(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [selectedLockRow, setSelectedLockRow] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [selectedUnlockRow, setSelectedUnlockRow] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [selectedRemoveRow, setSelectedRemoveRow] = useState(null);

  const [restaurantList, setRestaurantList] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [selectedFinalizeRow, setSelectedFinalizeRow] = useState(null);
  const [unfinalizeOpen, setUnfinalizeOpen] = useState(false);
  const [selectedUnfinalizeRow, setSelectedUnfinalizeRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
const [selectedEditRow, setSelectedEditRow] = useState(null);

  /* ================= FETCH ================= */

  const fetchReport = async (restIdParam) => {
    if (!fromDate || !toDate || !restIdParam) return;

    try {
      setLoading(true);
      setData([]); // ✅ clear old data immediately

      const res = await axiosSalary.get(
        `/DailyAttendance/GetExceptionReport?groupId=${groupId}&restId=${restIdParam}&fromDate=${fromDate}&toDate=${toDate}`
      );

      setData(res.data || []);

    } catch (err) {
      console.error("Exception report error:", err);
    } finally {
      setLoading(false);
    }
  };



  const fetchRestaurants = async () => {
    try {
      const res = await axiosSalary.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const options = (res.data || []).map(r => ({
        value: r.restId,
        label: r.restaurantName
      }));

      setRestaurantList(options);

      const savedRestId = localStorage.getItem("restaurantId");

      const defaultRest =
        options.find(o => o.value == savedRestId) || options[0];

      setSelectedRestaurant(defaultRest);

      // if (defaultRest?.value) {
      //   fetchReport(defaultRest.value); // ✅ initial load
      // }

    } catch (err) {
      console.error("Restaurant fetch error:", err);
    }
  };

  useEffect(() => {
    if (groupId) {
      fetchRestaurants();
    }
  }, [groupId]);

  const handleRestaurantChange = (selected) => {
    setSelectedRestaurant(selected);

    if (selected?.value) {
      localStorage.setItem("restaurantId", selected.value); // ✅ save first
      fetchReport(selected.value); // ✅ immediate fetch
    }
  };


  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    if (selectedRestaurant?.value && fromDate && toDate) {
      fetchReport(selectedRestaurant.value);
    }
  }, [fromDate, toDate, selectedRestaurant?.value]);

  /* ================= OPEN MODAL ================= */
  const openSessionModal = (row) => {
    setSelectedRow(row);
    setSessionOpen(true);
  };

  const openReplaceModal = (row) => {
    setSelectedReplaceRow(row);
    setReplaceOpen(true);
  };

  const openLockModal = (row) => {
    setSelectedLockRow(row);
    setLockOpen(true);
  };

  const openUnlockModal = (row) => {
    setSelectedUnlockRow(row);
    setUnlockOpen(true);
  };

  const openRemoveModal = (row) => {
    setSelectedRemoveRow(row);
    setRemoveOpen(true);
  };

  const openFinalizeModal = (row) => {
    setSelectedFinalizeRow(row);
    setFinalizeOpen(true);
  };

  const openUnfinalizeModal = (row) => {
    setSelectedUnfinalizeRow(row);
    setUnfinalizeOpen(true);
  };

  const openEditModal = (row) => {
  setSelectedEditRow(row);
  setEditOpen(true);
};
  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    return data.filter((d) =>
      d.empName?.toLowerCase().includes(term) ||
      String(d.empId).includes(term) ||
      d.remarks?.toLowerCase().includes(term)
    );
  }, [data, search]);

  /* ================= SUMMARY ================= */
  const summary = useMemo(() => {
    let late = 0,
      earlyLeave = 0,
      halfDay = 0,
      suspense = 0,
      absent = 0;

    filteredData.forEach(d => {
      if (d.isAbsent) absent++;   // ✅ NEW
      if (d.isLate) late++;
      if (d.isEarlyLeaving) earlyLeave++;
      if (d.isHalfDay) halfDay++;
      if (d.isSuspense) suspense++;
    });

    return {
      total: filteredData.length,
      late,
      earlyLeave,
      halfDay,
      suspense,
      absent   // ✅ NEW
    };
  }, [filteredData]);

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map(r => ({
      EmpId: r.empId,
      Name: `"${r.empName || ""}"`,
      Date: r.attendanceDate,
      FirstPunch: r.firstPunchIst || "",
      LastPunch: r.lastPunchIst || "",
      Punches: r.totalPunches,
      WorkingMinutes: r.workingMinutes,
      LateMinutes: r.lateMinutes || 0,
      EarlyLeaveMinutes: r.earlyLeaveMinutes || 0,
      OvertimeMinutes: r.overtimeMinutes,
      Status: getStatusText(r),
      Remarks: `"${r.remarks || ""}"`
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map(e => Object.values(e).join(","))
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `ExceptionReport_${fromDate}_to_${toDate}.csv`;
    link.click();
  };

  /* ================= STATUS ================= */
  const getStatusText = (row) => {
    if (row.isAbsent) return "Absent";
    if (row.isSuspense) return "Suspense";
    if (row.isHalfDay) return "Half Day";
    if (row.isEarlyLeaving) return "Early Leave";
    if (row.isLate) return "Late";
    return "Present";
  };

  const getStatusBadge = (row) => {
    if (row.isSuspense)
      return <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">Suspense</span>;
    if (row.isHalfDay)
      return <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Half Day</span>;
    if (row.isEarlyLeaving)
      return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">Early Leave</span>;
    if (row.isLate)
      return <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">Late</span>;

    return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">Normal</span>;
  };




  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">

      {/* HEADER */}
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-3">

        <h2 className="text-lg font-semibold">Attendance Exception Report</h2>

        <div className="flex flex-wrap gap-2 items-center ml-auto">

          {/* Restaurant Select */}
          <Select
            value={selectedRestaurant}
            onChange={handleRestaurantChange} // ✅ FIXED
            options={restaurantList}
            placeholder="Select Restaurant"
            className="text-sm min-w-[180px]"
          />

          {/* 🔍 Search */}
          <div className="relative">
            <FaSearch className="absolute left-2 top-2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 pr-2 py-1.5 border rounded text-sm"
            />
          </div>

          {/* Dates */}
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-2 py-1 rounded text-sm"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-2 py-1 rounded text-sm"
          />

          {/* ✅ EXPORT BUTTON ADDED */}
          <button
            onClick={exportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-2"
          >
            <FaFileExcel />
            Export
          </button>

        </div>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-6 gap-2 text-xs md:text-sm mb-3">
        <div className="bg-gray-100 p-2 rounded text-center">
          <p>Total</p><p>{summary.total}</p>
        </div>

        <div className="bg-yellow-100 p-2 rounded text-center">
          <p>Late</p><p>{summary.late}</p>
        </div>

        <div className="bg-red-100 p-2 rounded text-center">
          <p>Early Leave</p><p>{summary.earlyLeave}</p>
        </div>

        <div className="bg-orange-100 p-2 rounded text-center">
          <p>Half Day</p><p>{summary.halfDay}</p>
        </div>

        <div className="bg-purple-100 p-2 rounded text-center">
          <p>Suspense</p><p>{summary.suspense}</p>
        </div>

        <div className="bg-gray-300 p-2 rounded text-center">
          <p>Absent</p><p>{summary.absent}</p>
        </div>
      </div>

      {/* DESKTOP */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2">Name</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">First Punch</th>
              <th className="border p-2">Last Punch</th>
              <th className="border p-2">Punch</th>
              <th className="border p-2">Work Min</th>
              <th className="border p-2">OT Min</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Remarks</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map(row => (
              <tr key={row.empId + row.attendanceDate}>

                <td className="border p-2">{row.empId}</td>
                <td className="border p-2">{row.empName}</td>
                <td className="border p-2">{row.attendanceDate}</td>

                {/* Punch Info */}
                <td className="border p-2">{row.firstPunchIst || "-"}</td>
                <td className="border p-2">{row.lastPunchIst || "-"}</td>

                {/* Punch Count */}
                <td className="border p-2 text-center">
                  <span className={`px-2 py-1 rounded text-xs ${row.totalPunches % 2 !== 0
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                    }`}>
                    {row.totalPunches}
                  </span>
                </td>

                {/* Minutes directly */}
                <td className="border p-2">{row.workingMinutes}</td>
                <td className="border p-2 text-blue-600">{row.overtimeMinutes}</td>

                {/* Status */}
                <td className="border p-2">{getStatusBadge(row)}</td>

                {/* Remarks */}
                <td className="border p-2 text-xs">
                  {row.remarks}
                  {row.isManualEdited && (
                    <div className="text-orange-500">Edited</div>
                  )}
                </td>

                {/* ACTION */}
                <td className="border p-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <button
  onClick={() => openEditModal(row)}
  className="bg-indigo-600 text-white px-2 py-1 rounded text-xs"
>
  Edit Session
</button>

                    {/* ✅ IF ABSENT → ONLY + SESSION */}
                    {row.isAbsent ? (
                      <button
                        onClick={() => openSessionModal(row)}
                        className="bg-blue-600 text-white px-2 py-1 rounded text-xs"
                      >
                        + Session
                      </button>
                    ) : (
                      <>
                        {/* Replace → only odd punches */}
                        {row.totalPunches % 2 !== 0 && (
                          <button
                            onClick={() => openReplaceModal(row)}
                            className="bg-orange-600 text-white px-2 py-1 rounded text-xs"
                          >
                            Replace
                          </button>
                        )}

                        {/* Remove */}
                        <button
                          onClick={() => openRemoveModal(row)}
                          className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                        >
                          Remove Session
                        </button>

                        {/* Lock / Unlock */}
                        {row.isLocked ? (
                          <button
                            onClick={() => openUnlockModal(row)}
                            className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                          >
                            Unlock
                          </button>
                        ) : (
                          <button
                            onClick={() => openLockModal(row)}
                            className="bg-red-600 text-white px-2 py-1 rounded text-xs"
                          >
                            Lock
                          </button>
                        )}
                      </>
                    )}
                    {row.isFinalized ? (
                      <button
                        onClick={() => openUnfinalizeModal(row)}
                        className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                      >
                        Unfinalize
                      </button>
                    ) : (
                      <button
                        onClick={() => openFinalizeModal(row)}
                        className="bg-blue-700 text-white px-2 py-1 rounded text-xs"
                      >
                        Finalize
                      </button>
                    )}
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE */}
      <div className="md:hidden space-y-3 mt-3">
        <div className="md:hidden space-y-3 mt-3">
          {filteredData.map(row => (
            <div
              key={row.empId + row.attendanceDate}
              className="border p-3 rounded-xl shadow-sm bg-white"
            >

              {/* Header */}
              <div className="flex justify-between items-center mb-1">
                <h3 className="font-semibold text-sm">{row.empName}</h3>
                {getStatusBadge(row)}
              </div>

              {/* Basic Info */}
              <div className="text-xs text-gray-600">
                <div>ID: {row.empId}</div>
                <div>Date: {row.attendanceDate}</div>
              </div>

              {/* Punch Info */}
              <div className="mt-2 text-xs">
                <div>First Punch: {row.firstPunchIst || "-"}</div>
                <div>Last Punch: {row.lastPunchIst || "-"}</div>
              </div>

              {/* Punch Count */}
              <div className="mt-2">
                <span className={`px-2 py-1 rounded text-xs ${row.totalPunches % 2 !== 0
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
                  }`}>
                  Punch: {row.totalPunches}
                </span>
              </div>

              {/* Work + OT */}
              <div className="mt-2 text-xs flex justify-between">
                <span>Work: {row.workingMinutes}</span>
                <span className="text-blue-600">OT: {row.overtimeMinutes}</span>
              </div>

              {/* Remarks */}
              <div className="mt-2 text-xs text-gray-700">
                {row.remarks}
                {row.isManualEdited && (
                  <div className="text-orange-500">Edited</div>
                )}
              </div>

              {/* ACTION BUTTONS */}
              <div className="mt-3 space-y-1">

                <button
  onClick={() => openEditModal(row)}
  className="bg-indigo-600 text-white px-2 py-1 rounded text-xs"
>
  Edit Session
</button>

                {row.isAbsent ? (
                  <button
                    onClick={() => openSessionModal(row)}
                    className="w-full bg-blue-600 text-white py-1 rounded text-xs"
                  >
                    + Add Session
                  </button>
                ) : (
                  <>
                    {row.totalPunches % 2 !== 0 && (
                      <button
                        onClick={() => openReplaceModal(row)}
                        className="w-full bg-orange-600 text-white py-1 rounded text-xs"
                      >
                        Replace Session
                      </button>
                    )}

                    <button
                      onClick={() => openRemoveModal(row)}
                      className="w-full bg-red-500 text-white py-1 rounded text-xs"
                    >
                      Remove Session
                    </button>

                    {row.isLocked ? (
                      <button
                        onClick={() => openUnlockModal(row)}
                        className="w-full bg-green-600 text-white py-1 rounded text-xs"
                      >
                        Unlock Attendance
                      </button>
                    ) : (
                      <button
                        onClick={() => openLockModal(row)}
                        className="w-full bg-red-600 text-white py-1 rounded text-xs"
                      >
                        Lock Attendance
                      </button>
                    )}
                  </>
                )}
                {row.isFinalized ? (
                  <button
                    onClick={() => openUnfinalizeModal(row)}
                    className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                  >
                    Unfinalize
                  </button>
                ) : (
                  <button
                    onClick={() => openFinalizeModal(row)}
                    className="bg-blue-700 text-white px-2 py-1 rounded text-xs"
                  >
                    Finalize
                  </button>
                )}
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* ✅ MODAL */}
      <AddSessionByManager
        open={sessionOpen}
        onClose={() => setSessionOpen(false)}
        rowData={selectedRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />

      <ReplaceSessionByManager
        open={replaceOpen}
        onClose={() => setReplaceOpen(false)}
        rowData={selectedReplaceRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />
      <LockAttendance
        open={lockOpen}
        onClose={() => setLockOpen(false)}
        rowData={selectedLockRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />

      <UnlockAttendance
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        rowData={selectedUnlockRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />

      <RemoveSessionByManager
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        rowData={selectedRemoveRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />

      <FinalizeAttendance
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        rowData={selectedFinalizeRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />

      <UnfinalizeAttendance
        open={unfinalizeOpen}
        onClose={() => setUnfinalizeOpen(false)}
        rowData={selectedUnfinalizeRow}
        onSuccess={() => fetchReport(selectedRestaurant?.value)}
      />

      <EditSessionByManager
  open={editOpen}
  onClose={() => setEditOpen(false)}
  rowData={selectedEditRow}
  onSuccess={() => fetchReport(selectedRestaurant?.value)}
/>
    </div>
  );
};

export default AttendanceExceptionReport;